import React from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import PropTypes from "prop-types";

// Material imports
import { ThemeProvider } from "@mui/material/styles";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
// Limitless imports
import Grid from "@limitless/modern/layout/Grid";
import Typography from "@limitless/modern/data-display/typography/Typography";
import Chip from "@limitless/modern/data-display/chip/Chip";
import Avatar from "@limitless/modern/data-display/avatar/Avatar";
import { LoadingSpinner } from "@limitless/portal";
import { locale as translatableLocale } from "@limitless/common/utils";

import { useNotificationContext, useTranslation } from "@limitless/common/hooks";
import {
    validateMessageId,
    INITIAL_MESSAGE_RESOURCES,
    calculateMessageState,
    ConversationDialog
} from "@limitless/portal";
// Limitless Icons
import AccordionIcon from "@limitless/icons/AngleDown";
import { useExpertProfileContext } from "@limitless/expert";
import ModernTenantAvatar from "../../modern-task-list/modern-tenant-avatar/ModernTenantAvatar";
import Divider from "@limitless/modern/data-display/divider/Divider";

const textButtonStyles = { cursor: "pointer", textTransform: "capitalize", textAlign: "center", display: "block" };

const classes = {
    textButtonProps: {
        variant: "textButton",
        color: "primary.main",
        sx: { textButtonStyles },

        style: { alignSelf: "center", cursor: "pointer" }
    }
};

const RewardDetails = ({ theme, rewardQuestion, expandedItem, handleAccordionChange, isPendingReward = false }) => {
    const [message, setMessage] = React.useState();
    const { formatCurrency, formatDate, profile } = useExpertProfileContext();
    const { addSuccessNotification, addFailureNotification } = useNotificationContext();
    const locale = React.useMemo(() => translatableLocale(t, true), []);

    const { t } = useTranslation();

    const AccordionStyle = {
        maxWidth: "unset",
        boxShadow: "none",
        "&:before": {
            backgroundColor: "transparent !important"
        },
        minHeight: "36px",
        marginBottom: "16px"
    };

    return (
        <>
            {rewardQuestion?.loading ? (
                <Grid container justifyContent="center">
                    <LoadingSpinner color="primary" />
                </Grid>
            ) : rewardQuestion?.history.length === 0 ? (
                <Grid
                    item
                    xs={12}
                    component={Typography}
                    variant="subtitle2"
                    sx={{ paddingBottom: 1, textAlign: "center", marginTop: 5 }}
                    color="text.secondary"
                >
                    {isPendingReward
                        ? "You currently have no pending rewards"
                        : "You currently have no available rewards"}
                </Grid>
            ) : (
                rewardQuestion.filtered.map((item, index) => {
                    const isIncentive = item.type === "Incentive";

                    return (
                        <>
                            {/* {isPendingReward && (
                                <Typography variant="subtitle2" color="text.primary">
                                    {key}
                                </Typography>
                            )} */}
                            <>
                                <Accordion
                                    sx={AccordionStyle}
                                    elevation={0}
                                    key={`${item.groupName}${index}`}
                                    index={index}
                                    expanded={!isIncentive && expandedItem.includes(index)}
                                    onChange={() => handleAccordionChange(index)}
                                    id={`pendingAccordion${item.messageId}`}
                                >
                                    <AccordionSummary
                                        sx={{
                                            paddingLeft: theme => theme.spacing(1),
                                            paddingRight: theme => theme.spacing(1)
                                        }}
                                        expandIcon={
                                            !isIncentive ? (
                                                <AccordionIcon
                                                    sx={{
                                                        marginTop:
                                                            !isIncentive && expandedItem.includes(index)
                                                                ? "1px"
                                                                : "2px",
                                                        height: "26px",
                                                        color: theme.palette.text.primary
                                                    }}
                                                />
                                            ) : (
                                                <div style={{ height: "24px", width: "24px" }} />
                                            )
                                        }
                                    >
                                        <Grid
                                            container
                                            alignItems="center"
                                            sx={{ marginBottom: theme => theme.spacing(0) }}
                                        >
                                            <Grid
                                                container
                                                item
                                                xs={4}
                                                sm={8}
                                                md={6}
                                                lg={3}
                                                variant="subtitle2"
                                                display="flex"
                                                alignItems="center"
                                            >
                                                <Grid item xs={1}>
                                                    {/* <Avatar>
                                                            <Typography variant="subtitle2" color="text.primary">
                                                                {item.groupName.slice(0, 2)}
                                                            </Typography>
                                                        </Avatar> */}
                                                    <ModernTenantAvatar tenantName={item?.tenantName} />
                                                </Grid>
                                                <Grid item xs={11}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ paddingLeft: 2.5 }}
                                                        color="text.primary"
                                                        id={`pendingGroupName${item.messageId}`}
                                                    >
                                                        {item.displayName || item.groupName}
                                                    </Typography>
                                                </Grid>
                                            </Grid>

                                            <Grid
                                                item
                                                md={6}
                                                lg={3}
                                                xs={4}
                                                sm={4}
                                                component={Typography}
                                                variant="body2"
                                                color="text.primary"
                                                sx={{
                                                    textTransform: "capitalize",
                                                    textAlign: "center"
                                                }}
                                                id={`pendingType${item.messageId}`}
                                            >
                                                {isPendingReward ? item.type : item.type}
                                            </Grid>

                                            {isPendingReward && (
                                                <Grid item xs={2} sm={4} md={3} lg={2} sx={{ textAlign: "center" }}>
                                                    <Chip
                                                        label={t(
                                                            item.lifecycleState === "resolved"
                                                                ? "Awaiting payment"
                                                                : "In progress"
                                                        )}
                                                        sx={{
                                                            backgroundColor: theme =>
                                                                item.lifecycleState === "resolved"
                                                                    ? theme.palette.secondary.light
                                                                    : theme.palette.warning.light,
                                                            minWidth: 126
                                                        }}
                                                        id={`pendingState${item.messageId}`}
                                                    />
                                                </Grid>
                                            )}
                                            {!isPendingReward && (
                                                <Grid
                                                    item
                                                    md={3}
                                                    lg={2}
                                                    xs={2}
                                                    sm={4}
                                                    component={Typography}
                                                    sx={{ textAlign: "center" }}
                                                    variant="body2"
                                                    id={`pendingFee${item.messageId}`}
                                                >
                                                    {item.formatReportDate}
                                                </Grid>
                                            )}
                                            <Grid
                                                item
                                                xs={2}
                                                sm={4}
                                                md={3}
                                                lg={2}
                                                component={Typography}
                                                sx={{ textAlign: "center" }}
                                                variant="subtitle2"
                                                id={`pendingFee${item.messageId}`}
                                            >
                                                {formatCurrency(item.fee || "-")}
                                            </Grid>

                                            <Grid item lg={2} md={1} xs={0} sm={4} sx={{ textAlign: "center" }}>
                                                <Typography
                                                    {...classes.textButtonProps}
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        validateMessageId(item.messageId, message => {
                                                            if (message) {
                                                                const messageState = calculateMessageState(
                                                                    profile,
                                                                    message,
                                                                    INITIAL_MESSAGE_RESOURCES
                                                                );
                                                                setMessage(messageState);
                                                            } else {
                                                                addFailureNotification(
                                                                    "Message is not available to be viewed right now"
                                                                );
                                                            }
                                                        });
                                                    }}
                                                >
                                                    View Task
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </AccordionSummary>
                                    <AccordionDetails
                                        sx={{
                                            padding: "8px 36px 0px 0px",
                                            display: "flex",
                                            flexDirection: "row"
                                        }}
                                    >
                                        <Grid
                                            container
                                            item
                                            sm={12}
                                            xs={isPendingReward ? 6 : 4}
                                            md={isPendingReward ? 8 : 6}
                                            lg={isPendingReward ? 6 : 3.4}
                                            variant="subtitle2"
                                            display="flex"
                                            sx={{ paddingLeft: isPendingReward ? "12px" : "44px" }}
                                            alignItems="center"
                                        >
                                            {isPendingReward ? (
                                                <>
                                                    <Grid
                                                        item
                                                        variant="caption"
                                                        xs={12}
                                                        id={`pendingContributors${item.messageId}`}
                                                    >
                                                        <Typography variant="caption">
                                                            Number of contributors:
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{ fontWeight: 700, marginLeft: 0.5 }}
                                                        >
                                                            {item.contributorsCount}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid
                                                        item
                                                        variant="caption"
                                                        xs={12}
                                                        sx={{ marginTop: "12px" }}
                                                        id={`pendingAllocDate${item.messageId}`}
                                                    >
                                                        <Typography variant="caption">
                                                            Fee allocation on or before:
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{ fontWeight: 700, marginLeft: 0.5 }}
                                                        >
                                                            {item.expectedPaymentDate
                                                                ? formatDate(item.expectedPaymentDate, locale, true)
                                                                : "Not yet available"}
                                                        </Typography>
                                                    </Grid>
                                                </>
                                            ) : (
                                                item.type === "Responded" && (
                                                    <Grid>
                                                        <Typography variant="caption">Fee share:</Typography>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{ fontWeight: 700, marginLeft: 0.5 }}
                                                        >
                                                            {item.formattedProportion}
                                                        </Typography>
                                                    </Grid>
                                                )
                                            )}
                                        </Grid>
                                        <Grid
                                            item
                                            lg={2.3}
                                            md={3}
                                            xs={4}
                                            variant="body2"
                                            color="text.primary"
                                            sx={{
                                                textTransform: "capitalize",
                                                textAlign: "center",
                                                marginTop: isPendingReward ? "12px" : 0
                                            }}
                                            id={`pendingType${item.messageId}`}
                                        >
                                            <CopyToClipboard text={item.messageId}>
                                                <Typography
                                                    {...classes.textButtonProps}
                                                    onClick={() =>
                                                        addSuccessNotification("Task ID copied to clipboard")
                                                    }
                                                >
                                                    Copy Task ID
                                                </Typography>
                                            </CopyToClipboard>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>
                                <Divider
                                    sx={{
                                        backgroundColor: theme.palette.background.default,
                                        height: "1px",
                                        borderStyle: "none",
                                        borderColor: "transparent"
                                    }}
                                />
                            </>
                        </>
                    );
                })
            )}

            {message && (
                <ThemeProvider theme={theme}>
                    <ConversationDialog
                        headerText="Conversation"
                        message={message}
                        onCancelClick={() => setMessage()}
                        buttonVariant="contained"
                        buttonColor="secondary"
                    />
                </ThemeProvider>
            )}
        </>
    );
};

RewardDetails.propTypes = {
    expandedItem: PropTypes.array,
    handleAccordionChange: PropTypes.func,
    rewardQuestion: PropTypes.object.isRequired,
    expandedAllItem: PropTypes.bool,
    isPendingReward: PropTypes.bool,
    theme: PropTypes.object
};

export default RewardDetails;
